import { useIntl } from "react-intl";
import { shallowEqual, useSelector } from "react-redux";
import { matchPath, useLocation } from "react-router-dom";
import { parse, stringify } from "qs";
import { PRODUCTS_VISIBILITY, STRATEGY } from "app/constants";
import {
	BREADCRUMB_ORIGIN_FROM_PARAM,
	BREADCRUMB_ORIGIN_PATH_PARAM,
} from "app/features/breadcrumb";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { stripAllTags, unescapeHtml } from "app/utils/html/html";
import { isAbsoluteUrl } from "app/utils/urlDataExtractor";

const LISTING_PATH = "/listing";
const MERCH_PATH = "/merch/:merchCode";
const LANDING_PAGE_PATH = "/landing-pages/:landingPageCode/*";
const NEW_TAB_REL = "noopener noreferrer";

export type ProductLinkUtm = {
	medium?: string;
	source?: string;
	campaign?: string;
	content?: string;
	term?: string;
};

export type BuildProductLinkParams = {
	productUri?: string;
	query?: Record<string, unknown>;
	openOnNewTab?: boolean;
	pathname?: string;
	resolution?: string;
	productsVisibility?: string;
	strategy?: string;
	source?: string;
	utm?: ProductLinkUtm;
	listingLabel?: string;
	merchTitle?: string;
	landingPageTitle?: string;
};

export type ProductLinkProps = {
	to: { pathname?: string; search?: string };
	target?: "_blank";
	rel?: string;
};

const UTM_PARAMETERS = {
	medium: "utm_medium",
	source: "utm_source",
	campaign: "utm_campaign",
	content: "utm_content",
	term: "utm_term",
} as const;

const isKnownStrategy = (strategy?: string) =>
	Object.values<string>(STRATEGY).some(known => known === strategy);

const normalizeProductUrl = (productUri?: string) => {
	if (productUri && !isAbsoluteUrl(productUri) && !productUri.startsWith("/")) {
		return `/${productUri}`;
	}

	return productUri;
};

const buildUtmSearch = (utm: ProductLinkUtm) =>
	Object.fromEntries(
		(Object.keys(UTM_PARAMETERS) as (keyof typeof UTM_PARAMETERS)[])
			.filter(key => utm[key])
			.map(key => [UTM_PARAMETERS[key], utm[key]])
	);

type BreadcrumbOrigin = { from: string; path: string };

type BreadcrumbOriginParams = Pick<
	BuildProductLinkParams,
	"listingLabel" | "merchTitle" | "landingPageTitle"
> & { pathname: string; merchCode?: string };

const getBreadcrumbOrigin = ({
	pathname,
	merchCode,
	listingLabel,
	merchTitle,
	landingPageTitle,
}: BreadcrumbOriginParams): BreadcrumbOrigin | undefined => {
	if (matchPath({ path: LISTING_PATH }, pathname)) {
		return listingLabel ? { from: listingLabel, path: LISTING_PATH } : undefined;
	}

	if (merchCode && merchTitle) {
		return { from: merchTitle, path: `/merch/${merchCode}` };
	}

	const landingPageCode = matchPath({ path: LANDING_PAGE_PATH }, pathname)?.params
		?.landingPageCode;

	if (landingPageCode && landingPageTitle) {
		return { from: landingPageTitle, path: `/landing-pages/${landingPageCode}` };
	}

	return undefined;
};

/** Builds the link to a product page, carrying over the preview, strategy, merch, marketing and breadcrumb origin context. */
export const buildProductLink = ({
	productUri,
	query = {},
	openOnNewTab,
	pathname = "",
	resolution,
	productsVisibility,
	strategy,
	source,
	utm = {},
	listingLabel,
	merchTitle,
	landingPageTitle,
}: BuildProductLinkParams): ProductLinkProps => {
	const productUrl = normalizeProductUrl(productUri);
	const [path, existingQuery] = productUrl ? productUrl.split("?") : [];
	const isDesktop = resolution === RESOLUTION.LARGE || resolution === RESOLUTION.XLARGE;
	const merchCode = matchPath({ path: MERCH_PATH }, pathname)?.params?.merchCode;
	const breadcrumbOrigin = getBreadcrumbOrigin({
		pathname,
		merchCode,
		listingLabel,
		merchTitle,
		landingPageTitle,
	});

	const uriSearch: Record<string, unknown> = productUrl
		? { ...(existingQuery ? parse(existingQuery, { ignoreQueryPrefix: true }) : {}), ...query }
		: {};

	const search = {
		...uriSearch,
		...(productsVisibility === PRODUCTS_VISIBILITY.PREVIEW && {
			preview: PRODUCTS_VISIBILITY.PREVIEW,
		}),
		...(openOnNewTab && isKnownStrategy(strategy) && !uriSearch.strategy && { strategy }),
		...(merchCode && isDesktop && { fromMerch: merchCode }),
		...(source && { source }),
		...buildUtmSearch(utm),
		...(breadcrumbOrigin && {
			[BREADCRUMB_ORIGIN_FROM_PARAM]: breadcrumbOrigin.from,
			[BREADCRUMB_ORIGIN_PATH_PARAM]: breadcrumbOrigin.path,
		}),
	};

	const to: ProductLinkProps["to"] = {
		...(path && { pathname: path }),
		...(Object.keys(search).length > 0 && { search: stringify(search) }),
	};

	return openOnNewTab ? { to, target: "_blank", rel: NEW_TAB_REL } : { to };
};

type WithProductLinkState = {
	resolution: string;
	productsVisibility: string;
	partner: { strategy: string };
	marketing?: {
		source?: string;
		utmMedium?: string;
		utmSource?: string;
		utmCampaign?: string;
		utmContent?: string;
		utmTerm?: string;
	};
	merchandising?: {
		current?: { displayOptions?: { smartDP?: { merchandisingTitle?: string } } };
	};
	landingPage?: {
		current?: { layout?: { merchandisingTitleDesktop?: string } };
	};
};

const toPlainText = (html = "") => unescapeHtml(stripAllTags(html));

const selectProductLinkContext = (state: WithProductLinkState) => ({
	resolution: state.resolution,
	productsVisibility: state.productsVisibility,
	strategy: state.partner.strategy,
	source: state.marketing?.source,
	utmMedium: state.marketing?.utmMedium,
	utmSource: state.marketing?.utmSource,
	utmCampaign: state.marketing?.utmCampaign,
	utmContent: state.marketing?.utmContent,
	utmTerm: state.marketing?.utmTerm,
	merchTitle: toPlainText(
		state.merchandising?.current?.displayOptions?.smartDP?.merchandisingTitle
	),
	landingPageTitle: toPlainText(state.landingPage?.current?.layout?.merchandisingTitleDesktop),
});

type UseProductLinkParams = Pick<BuildProductLinkParams, "productUri" | "query" | "openOnNewTab">;

/** Link props (`to`, and `target` / `rel` for a new tab) of a product page, built from the store, the location and the breadcrumb origin. */
export const useProductLink = ({ productUri, query, openOnNewTab }: UseProductLinkParams) => {
	const { pathname } = useLocation();
	const { formatMessage } = useIntl();
	const { utmMedium, utmSource, utmCampaign, utmContent, utmTerm, ...context } = useSelector(
		selectProductLinkContext,
		shallowEqual
	);

	return buildProductLink({
		productUri,
		query,
		openOnNewTab,
		pathname,
		listingLabel: formatMessage({ id: "breadcrumb.listing.label" }),
		...context,
		utm: {
			medium: utmMedium,
			source: utmSource,
			campaign: utmCampaign,
			content: utmContent,
			term: utmTerm,
		},
	});
};
